<template>
  <AdminLayout>
    <div class="travel-agencies-container">
      <!-- Header Row -->
      <div class="page-header-custom">
        <h1 class="page-title">Reisbureaus</h1>
        <button @click="openAddModal" class="btn-primary">
          + Nieuw Reisbureau
        </button>
      </div>

      <!-- Search, Filter and View Toggle -->
      <div class="search-filter-bar">
        <div class="search-box">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Zoek op naam, adres, email..."
            class="search-input"
          />
        </div>
        <div class="filter-buttons">
          <button
            @click="filterStatus = 'all'"
            :class="['filter-btn', { active: filterStatus === 'all' }]"
          >
            Alle ({{ agencies.length }})
          </button>
          <button
            @click="filterStatus = 'active'"
            :class="['filter-btn', { active: filterStatus === 'active' }]"
          >
            Actief ({{ activeCount }})
          </button>
          <button
            @click="filterStatus = 'inactive'"
            :class="['filter-btn', { active: filterStatus === 'inactive' }]"
          >
            Inactief ({{ inactiveCount }})
          </button>
        </div>
        <div class="view-toggle">
          <button
            @click="viewMode = 'list'"
            :class="['view-btn', { active: viewMode === 'list' }]"
            title="Lijstweergave"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"></path>
            </svg>
          </button>
          <button
            @click="viewMode = 'grid'"
            :class="['view-btn', { active: viewMode === 'grid' }]"
            title="Rasterweergave"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"></path>
            </svg>
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Reisbureaus laden...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredAgencies.length === 0" class="empty-state">
        <h3>Geen reisbureaus gevonden</h3>
        <p v-if="searchQuery">Probeer een andere zoekterm</p>
        <p v-else>Voeg een nieuw reisbureau toe om te beginnen</p>
      </div>

      <!-- List View -->
      <div v-else-if="viewMode === 'list'" class="agencies-list">
        <table class="agencies-table">
          <thead>
            <tr>
              <th>Naam</th>
              <th>Adres</th>
              <th>Telefoon</th>
              <th>Email</th>
              <th>Website</th>
              <th>Status</th>
              <th>Acties</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="agency in filteredAgencies"
              :key="agency.id"
              :class="{ inactive: !agency.isActive }"
            >
              <td class="name-cell">{{ agency.name }}</td>
              <td>
                <span v-if="agency.addressLine1">{{ agency.addressLine1 }}</span>
                <span v-if="agency.addressLine2"><br/>{{ agency.addressLine2 }}</span>
              </td>
              <td>{{ agency.phone || '-' }}</td>
              <td>{{ agency.email || '-' }}</td>
              <td>
                <a v-if="agency.website" :href="formatWebsite(agency.website)" target="_blank" class="website-link">
                  {{ agency.website }}
                </a>
                <span v-else>-</span>
              </td>
              <td>
                <span :class="['status-text', agency.isActive ? 'active' : 'inactive']">
                  {{ agency.isActive ? 'Actief' : 'Inactief' }}
                </span>
              </td>
              <td class="actions-cell">
                <button @click="openEditModal(agency)" class="action-btn" title="Bewerken">
                  Bewerken
                </button>
                <button
                  @click="toggleStatus(agency)"
                  class="action-btn"
                  :title="agency.isActive ? 'Deactiveren' : 'Activeren'"
                >
                  {{ agency.isActive ? 'Deactiveren' : 'Activeren' }}
                </button>
                <button @click="confirmDelete(agency)" class="action-btn delete" title="Verwijderen">
                  Verwijderen
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Grid View -->
      <div v-else class="agencies-grid">
        <div
          v-for="agency in filteredAgencies"
          :key="agency.id"
          :class="['agency-card', { inactive: !agency.isActive }]"
        >
          <!-- Agency Avatar -->
          <div class="agency-avatar">
            <img
              v-if="agency.avatar"
              :src="agency.avatar"
              :alt="agency.name"
              @error="handleImageError($event)"
            />
            <div v-else class="avatar-placeholder">
              {{ getInitials(agency.name) }}
            </div>
            <span :class="['status-badge', agency.isActive ? 'active' : 'inactive']">
              {{ agency.isActive ? 'Actief' : 'Inactief' }}
            </span>
          </div>

          <!-- Agency Info -->
          <div class="agency-info">
            <h3 class="agency-name">{{ agency.name }}</h3>

            <div class="info-row" v-if="agency.addressLine1">
              <span>{{ agency.addressLine1 }}<br v-if="agency.addressLine2" />{{ agency.addressLine2 }}</span>
            </div>

            <div class="info-row" v-if="agency.phone">
              <span>{{ agency.phone }}</span>
            </div>

            <div class="info-row" v-if="agency.email">
              <span>{{ agency.email }}</span>
            </div>

            <div class="info-row" v-if="agency.website">
              <a :href="formatWebsite(agency.website)" target="_blank" class="website-link">{{ agency.website }}</a>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="agency-actions">
            <button @click="openEditModal(agency)" class="action-btn" title="Bewerken">
              Bewerken
            </button>
            <button
              @click="toggleStatus(agency)"
              class="action-btn"
              :title="agency.isActive ? 'Deactiveren' : 'Activeren'"
            >
              {{ agency.isActive ? 'Deactiveren' : 'Activeren' }}
            </button>
            <button @click="confirmDelete(agency)" class="action-btn delete" title="Verwijderen">
              Verwijderen
            </button>
          </div>
        </div>
      </div>

      <!-- Add/Edit Modal -->
      <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
        <div class="modal-content">
          <div class="modal-header">
            <h2>{{ editingAgency ? 'Reisbureau Bewerken' : 'Nieuw Reisbureau' }}</h2>
            <button @click="closeModal" class="close-btn">&times;</button>
          </div>

          <form @submit.prevent="saveAgency" class="modal-form">
            <div class="form-grid">
              <!-- Name -->
              <div class="form-group full-width">
                <label for="name">Naam *</label>
                <input
                  id="name"
                  v-model="form.name"
                  type="text"
                  required
                  placeholder="Bijv. Selectair Reizen Sarlee"
                />
              </div>

              <!-- Avatar URL -->
              <div class="form-group full-width">
                <label for="avatar">Logo URL</label>
                <input
                  id="avatar"
                  v-model="form.avatar"
                  type="url"
                  placeholder="https://example.com/logo.jpg"
                />
                <small class="form-hint">URL naar het logo van het reisbureau</small>
              </div>

              <!-- Address Line 1 -->
              <div class="form-group">
                <label for="addressLine1">Adres</label>
                <input
                  id="addressLine1"
                  v-model="form.addressLine1"
                  type="text"
                  placeholder="Bijv. Kempische Kaai 9"
                />
              </div>

              <!-- Address Line 2 -->
              <div class="form-group">
                <label for="addressLine2">Postcode & Plaats</label>
                <input
                  id="addressLine2"
                  v-model="form.addressLine2"
                  type="text"
                  placeholder="Bijv. 3500 Hasselt"
                />
              </div>

              <!-- Phone -->
              <div class="form-group">
                <label for="phone">Telefoon</label>
                <input
                  id="phone"
                  v-model="form.phone"
                  type="tel"
                  placeholder="Bijv. 011/22 77 51"
                />
              </div>

              <!-- Fax -->
              <div class="form-group">
                <label for="fax">Fax</label>
                <input
                  id="fax"
                  v-model="form.fax"
                  type="tel"
                  placeholder="Optioneel"
                />
              </div>

              <!-- Email -->
              <div class="form-group">
                <label for="email">Email</label>
                <input
                  id="email"
                  v-model="form.email"
                  type="email"
                  placeholder="Bijv. reizen@sarlee.be"
                />
              </div>

              <!-- Website -->
              <div class="form-group">
                <label for="website">Website</label>
                <input
                  id="website"
                  v-model="form.website"
                  type="text"
                  placeholder="Bijv. www.sarleereizen.be"
                />
              </div>

              <!-- Coordinates -->
              <div class="form-group">
                <label for="latitude">Breedtegraad (Latitude)</label>
                <input
                  id="latitude"
                  v-model.number="form.latitude"
                  type="number"
                  step="any"
                  placeholder="Bijv. 50.934907"
                />
              </div>

              <div class="form-group">
                <label for="longitude">Lengtegraad (Longitude)</label>
                <input
                  id="longitude"
                  v-model.number="form.longitude"
                  type="number"
                  step="any"
                  placeholder="Bijv. 5.338435"
                />
              </div>

              <!-- Active Status -->
              <div class="form-group full-width">
                <label class="checkbox-label">
                  <input type="checkbox" v-model="form.isActive" />
                  <span class="checkbox-text">Reisbureau is actief</span>
                </label>
              </div>
            </div>

            <div class="modal-actions">
              <button type="button" @click="closeModal" class="btn-secondary">Annuleren</button>
              <button type="submit" class="btn-primary" :disabled="saving">
                <span v-if="saving">Opslaan...</span>
                <span v-else>{{ editingAgency ? 'Bijwerken' : 'Toevoegen' }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Delete Confirmation Modal -->
      <div v-if="showDeleteModal" class="modal-overlay" @click.self="showDeleteModal = false">
        <div class="modal-content delete-modal">
          <h3>Reisbureau Verwijderen</h3>
          <p>Weet je zeker dat je <strong>{{ agencyToDelete?.name }}</strong> wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.</p>
          <div class="modal-actions">
            <button @click="showDeleteModal = false" class="btn-secondary">Annuleren</button>
            <button @click="deleteAgency" class="btn-danger" :disabled="deleting">
              {{ deleting ? 'Verwijderen...' : 'Verwijderen' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Toast Notification -->
      <div v-if="toast.show" :class="['toast', toast.type]">
        {{ toast.message }}
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import AdminLayout from '@/components/AdminLayout.vue';

export default {
  name: 'TravelAgencies',
  components: {
    AdminLayout
  },
  data() {
    return {
      agencies: [],
      loading: true,
      searchQuery: '',
      filterStatus: 'all',
      viewMode: 'list',
      showModal: false,
      showDeleteModal: false,
      editingAgency: null,
      agencyToDelete: null,
      saving: false,
      deleting: false,
      form: {
        name: '',
        avatar: '',
        addressLine1: '',
        addressLine2: '',
        phone: '',
        fax: '',
        email: '',
        website: '',
        latitude: null,
        longitude: null,
        isActive: true
      },
      toast: {
        show: false,
        message: '',
        type: 'success'
      }
    };
  },
  computed: {
    shopSettings() {
      return this.$root.shopSettings || {};
    },
    apiUrl() {
      return this.shopSettings?.apiUrl || "https://selectair-vakanties.uptodatesites.com";
    },
    filteredAgencies() {
      let result = this.agencies;

      // Filter by status
      if (this.filterStatus === 'active') {
        result = result.filter(a => a.isActive);
      } else if (this.filterStatus === 'inactive') {
        result = result.filter(a => !a.isActive);
      }

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(a =>
          a.name?.toLowerCase().includes(query) ||
          a.addressLine1?.toLowerCase().includes(query) ||
          a.addressLine2?.toLowerCase().includes(query) ||
          a.email?.toLowerCase().includes(query) ||
          a.phone?.toLowerCase().includes(query)
        );
      }

      return result;
    },
    activeCount() {
      return this.agencies.filter(a => a.isActive).length;
    },
    inactiveCount() {
      return this.agencies.filter(a => !a.isActive).length;
    }
  },
  mounted() {
    this.fetchAgencies();
  },
  methods: {
    async fetchAgencies() {
      this.loading = true;
      try {
        const token = localStorage.getItem('admin_token');
        const response = await fetch(`${this.apiUrl}/api/admin/travel-agencies`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        if (data.success) {
          this.agencies = data.data;
        } else {
          this.showToast('Fout bij laden van reisbureaus', 'error');
        }
      } catch (error) {
        console.error('Error fetching agencies:', error);
        this.showToast('Fout bij laden van reisbureaus', 'error');
      } finally {
        this.loading = false;
      }
    },

    openAddModal() {
      this.editingAgency = null;
      this.resetForm();
      this.showModal = true;
    },

    openEditModal(agency) {
      this.editingAgency = agency;
      this.form = {
        name: agency.name || '',
        avatar: agency.avatar || '',
        addressLine1: agency.addressLine1 || '',
        addressLine2: agency.addressLine2 || '',
        phone: agency.phone || '',
        fax: agency.fax || '',
        email: agency.email || '',
        website: agency.website || '',
        latitude: agency.coordinates?.latitude || null,
        longitude: agency.coordinates?.longitude || null,
        isActive: agency.isActive !== false
      };
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.editingAgency = null;
      this.resetForm();
    },

    resetForm() {
      this.form = {
        name: '',
        avatar: '',
        addressLine1: '',
        addressLine2: '',
        phone: '',
        fax: '',
        email: '',
        website: '',
        latitude: null,
        longitude: null,
        isActive: true
      };
    },

    async saveAgency() {
      this.saving = true;
      try {
        const token = localStorage.getItem('admin_token');
        const url = this.editingAgency
          ? `${this.apiUrl}/api/admin/travel-agencies/${this.editingAgency.id}`
          : `${this.apiUrl}/api/admin/travel-agencies`;

        const response = await fetch(url, {
          method: this.editingAgency ? 'PUT' : 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.form)
        });

        const data = await response.json();
        if (data.success) {
          this.showToast(
            this.editingAgency ? 'Reisbureau bijgewerkt' : 'Reisbureau toegevoegd',
            'success'
          );
          this.closeModal();
          this.fetchAgencies();
        } else {
          this.showToast(data.message || 'Er is een fout opgetreden', 'error');
        }
      } catch (error) {
        console.error('Error saving agency:', error);
        this.showToast('Er is een fout opgetreden', 'error');
      } finally {
        this.saving = false;
      }
    },

    async toggleStatus(agency) {
      try {
        const token = localStorage.getItem('admin_token');
        const response = await fetch(
          `${this.apiUrl}/api/admin/travel-agencies/${agency.id}/toggle`,
          {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ isActive: !agency.isActive })
          }
        );

        const data = await response.json();
        if (data.success) {
          agency.isActive = !agency.isActive;
          this.showToast(
            agency.isActive ? 'Reisbureau geactiveerd' : 'Reisbureau gedeactiveerd',
            'success'
          );
        } else {
          this.showToast(data.message || 'Er is een fout opgetreden', 'error');
        }
      } catch (error) {
        console.error('Error toggling status:', error);
        this.showToast('Er is een fout opgetreden', 'error');
      }
    },

    confirmDelete(agency) {
      this.agencyToDelete = agency;
      this.showDeleteModal = true;
    },

    async deleteAgency() {
      if (!this.agencyToDelete) return;

      this.deleting = true;
      try {
        const token = localStorage.getItem('admin_token');
        const response = await fetch(
          `${this.apiUrl}/api/admin/travel-agencies/${this.agencyToDelete.id}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        const data = await response.json();
        if (data.success) {
          this.showToast('Reisbureau verwijderd', 'success');
          this.showDeleteModal = false;
          this.agencyToDelete = null;
          this.fetchAgencies();
        } else {
          this.showToast(data.message || 'Er is een fout opgetreden', 'error');
        }
      } catch (error) {
        console.error('Error deleting agency:', error);
        this.showToast('Er is een fout opgetreden', 'error');
      } finally {
        this.deleting = false;
      }
    },

    getInitials(name) {
      if (!name) return '?';
      return name
        .split(' ')
        .map(word => word[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
    },

    formatWebsite(url) {
      if (!url) return '#';
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }
      return `https://${url}`;
    },

    handleImageError(event) {
      event.target.style.display = 'none';
    },

    showToast(message, type = 'success') {
      this.toast = { show: true, message, type };
      setTimeout(() => {
        this.toast.show = false;
      }, 3000);
    }
  }
};
</script>

<style scoped>
.travel-agencies-container {
  padding: 0;
}

/* Header */
.page-header-custom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.page-title {
  font-size: 2.4rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.btn-primary {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 1rem 2rem;
  background: #333;
  color: white;
  border: none;
  border-radius: 0.4rem;
  font-size: 1.4rem;
  font-weight: 500;
  cursor: pointer;
}

.btn-primary:hover {
  background: #555;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Search and Filter */
.search-filter-bar {
  display: flex;
  gap: 1.2rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  align-items: center;
}

.search-box {
  flex: 1;
  min-width: 200px;
}

.search-input {
  width: 100%;
  padding: 1rem 1.2rem;
  border: 1px solid #ccc;
  border-radius: 0.4rem;
  font-size: 1.4rem;
}

.search-input:focus {
  outline: none;
  border-color: #666;
}

.filter-buttons {
  display: flex;
  gap: 0.6rem;
}

.filter-btn {
  padding: 1rem 1.4rem;
  background: white;
  border: 1px solid #ccc;
  border-radius: 0.4rem;
  font-size: 1.3rem;
  color: #666;
  cursor: pointer;
}

.filter-btn:hover {
  background: #f5f5f5;
}

.filter-btn.active {
  background: #333;
  border-color: #333;
  color: white;
}

/* View Toggle */
.view-toggle {
  display: flex;
  gap: 0.4rem;
  border: 1px solid #ccc;
  border-radius: 0.4rem;
  padding: 0.2rem;
}

.view-btn {
  padding: 0.6rem;
  background: transparent;
  border: none;
  border-radius: 0.3rem;
  cursor: pointer;
  color: #666;
}

.view-btn:hover {
  background: #f5f5f5;
}

.view-btn.active {
  background: #333;
  color: white;
}

.view-btn svg {
  width: 1.8rem;
  height: 1.8rem;
  display: block;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6rem;
  color: #666;
  font-size: 1.4rem;
}

.spinner {
  width: 4rem;
  height: 4rem;
  border: 3px solid #e0e0e0;
  border-top-color: #333;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1.5rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6rem;
  text-align: center;
  color: #666;
}

.empty-state h3 {
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 1.6rem;
  font-weight: 600;
}

.empty-state p {
  font-size: 1.4rem;
  margin: 0;
}

/* List View */
.agencies-list {
  background: white;
  border: 1px solid #ddd;
  border-radius: 0.4rem;
  overflow: hidden;
}

.agencies-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 1.3rem;
}

.agencies-table th,
.agencies-table td {
  padding: 1.2rem 1rem;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.agencies-table th {
  background: #f9f9f9;
  font-weight: 600;
  color: #333;
  font-size: 1.2rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.agencies-table tr:hover {
  background: #fafafa;
}

.agencies-table tr.inactive {
  opacity: 0.5;
}

.name-cell {
  font-weight: 500;
}

.status-text {
  font-size: 1.2rem;
}

.status-text.active {
  color: #333;
}

.status-text.inactive {
  color: #999;
}

.actions-cell {
  white-space: nowrap;
}

.action-btn {
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #ccc;
  border-radius: 0.3rem;
  font-size: 1.2rem;
  cursor: pointer;
  margin-right: 0.5rem;
}

.action-btn:hover {
  background: #f5f5f5;
}

.action-btn.delete {
  color: #666;
}

.action-btn.delete:hover {
  background: #f0f0f0;
}

.website-link {
  color: #333;
  text-decoration: underline;
}

.website-link:hover {
  color: #000;
}

/* Grid View */
.agencies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
}

.agency-card {
  background: white;
  border-radius: 0.4rem;
  border: 1px solid #ddd;
  overflow: hidden;
}

.agency-card:hover {
  border-color: #bbb;
}

.agency-card.inactive {
  opacity: 0.5;
}

.agency-avatar {
  position: relative;
  height: 100px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.agency-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  font-size: 2.4rem;
  font-weight: 600;
  color: #999;
}

.status-badge {
  position: absolute;
  top: 0.8rem;
  right: 0.8rem;
  padding: 0.3rem 0.8rem;
  border-radius: 0.3rem;
  font-size: 1.1rem;
  font-weight: 500;
  background: white;
  border: 1px solid #ddd;
}

.status-badge.active {
  color: #333;
}

.status-badge.inactive {
  color: #999;
}

.agency-info {
  padding: 1.4rem;
}

.agency-name {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 1rem 0;
}

.info-row {
  margin-bottom: 0.5rem;
  font-size: 1.3rem;
  color: #666;
  line-height: 1.4;
}

/* Card Action Buttons */
.agency-actions {
  display: flex;
  border-top: 1px solid #eee;
  padding: 1rem 1.4rem;
  gap: 0.6rem;
}

.agency-actions .action-btn {
  flex: 1;
  text-align: center;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
}

.modal-content {
  background: white;
  border-radius: 0.4rem;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  border-bottom: 1px solid #eee;
}

.modal-header h2 {
  font-size: 1.8rem;
  font-weight: 600;
  margin: 0;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 2.4rem;
  cursor: pointer;
  color: #999;
  line-height: 1;
}

.close-btn:hover {
  color: #333;
}

.modal-form {
  padding: 2rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.6rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  font-size: 1.3rem;
  font-weight: 500;
  color: #333;
}

.form-group input[type="text"],
.form-group input[type="email"],
.form-group input[type="tel"],
.form-group input[type="url"],
.form-group input[type="number"] {
  padding: 1rem 1.2rem;
  border: 1px solid #ccc;
  border-radius: 0.4rem;
  font-size: 1.4rem;
}

.form-group input:focus {
  outline: none;
  border-color: #666;
}

.form-hint {
  font-size: 1.2rem;
  color: #999;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  cursor: pointer;
  font-size: 1.4rem;
}

.checkbox-label input[type="checkbox"] {
  width: 1.8rem;
  height: 1.8rem;
}

.checkbox-text {
  font-weight: normal;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
}

.btn-secondary {
  padding: 1rem 2rem;
  background: white;
  border: 1px solid #ccc;
  border-radius: 0.4rem;
  font-size: 1.4rem;
  color: #333;
  cursor: pointer;
}

.btn-secondary:hover {
  background: #f5f5f5;
}

.btn-danger {
  padding: 1rem 2rem;
  background: #333;
  color: white;
  border: none;
  border-radius: 0.4rem;
  font-size: 1.4rem;
  cursor: pointer;
}

.btn-danger:hover {
  background: #555;
}

.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Delete Modal */
.delete-modal {
  max-width: 400px;
  text-align: center;
  padding: 3rem;
}

.delete-modal h3 {
  margin: 0 0 1rem 0;
  font-size: 1.8rem;
  color: #333;
}

.delete-modal p {
  color: #666;
  margin: 0 0 2rem 0;
  font-size: 1.4rem;
  line-height: 1.5;
}

.delete-modal .modal-actions {
  justify-content: center;
  border-top: none;
  padding-top: 0;
  margin-top: 0;
}

/* Toast */
.toast {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  padding: 1.2rem 2rem;
  border-radius: 0.4rem;
  color: white;
  font-weight: 500;
  font-size: 1.4rem;
  z-index: 1001;
  animation: slideIn 0.3s ease;
}

.toast.success {
  background: #333;
}

.toast.error {
  background: #666;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .page-header-custom {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
  }

  .search-filter-bar {
    flex-direction: column;
  }

  .filter-buttons {
    width: 100%;
  }

  .filter-btn {
    flex: 1;
    text-align: center;
  }

  .agencies-grid {
    grid-template-columns: 1fr;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full-width {
    grid-column: 1;
  }

  .agencies-table {
    font-size: 1.2rem;
  }

  .agencies-table th,
  .agencies-table td {
    padding: 1rem 0.6rem;
  }
}
</style>
